import React, { useCallback, useEffect, useRef, useState } from 'react';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import MessageCenter from './components/MessageCenter';
import AccountList from './components/AccountList';
import OrderList from './components/OrderList';
import CardList from './components/CardList';
import ItemList from './components/ItemList';
import AutoDelivery from './components/AutoDelivery';
import Settings from './components/Settings';
import Keywords from './components/Keywords';
import { ToastProvider } from './components/Toast';
import { connectChatEvents, getChatSessions, getSystemSettings, login, verifyToken } from './services/api';
import { ChatMessage } from './types';
import { ShieldCheck, ArrowRight, Loader2, Sparkles, User, Lock, KeyRound, ChevronDown, ChevronUp, X } from 'lucide-react';
import { AUTH_LOGOUT_EVENT } from './lib/request';

const getChatKey = (value: Pick<ChatMessage, 'cookie_id' | 'chat_id'>) => `${value.cookie_id}::${value.chat_id}`;

const isSettingEnabled = (value: unknown, fallback = true) => {
  if (value === undefined || value === null || value === '') return fallback;
  return ['true', '1', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

const messagePreview = (message: ChatMessage) => {
  if (message.message_type === 'image') return '[图片]';
  if (message.message_type === 'order_card') return '[订单卡片]';
  if (message.message_type === 'item_card') return '[商品卡片]';
  if (message.message_type === 'card') return '[卡片消息]';
  return message.content || '[新消息]';
};

const resolveNotificationAssetUrl = (value?: unknown) => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('//')) return `https:${trimmed}`;
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:') ||
    trimmed.startsWith('/')
  ) {
    return trimmed;
  }
  return `/${trimmed.replace(/^\/+/, '')}`;
};

const toAbsoluteNotificationUrl = (value?: string) => {
  if (!value) return '';
  if (value.startsWith('data:') || value.startsWith('blob:')) return value;
  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return value;
  }
};

const formatNotificationTime = (value?: string) => {
  const date = value ? new Date(value.replace(' ', 'T')) : new Date();
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return safeDate.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getNotificationAvatar = (message: ChatMessage) => {
  const metadata = message.metadata || {};
  return resolveNotificationAssetUrl(
    message.buyer_avatar ||
    metadata.buyer_avatar ||
    metadata.sender_avatar ||
    metadata.avatar_url
  );
};

type MessageNotificationItem = {
  id: string;
  sessionKey: string;
  senderName: string;
  content: string;
  time: string;
  rawTime?: string;
  avatar?: string;
};

const MessageNotificationCard: React.FC<{
  items: MessageNotificationItem[];
  activeIndex: number;
  onIndexChange: (index: number) => void;
  onOpen: (sessionKey: string) => void;
  onClose: () => void;
}> = ({ items, activeIndex, onIndexChange, onOpen, onClose }) => {
  const item = items[activeIndex];
  if (!item) return null;

  const hasMultiple = items.length > 1;
  const canGoNewer = activeIndex > 0;
  const canGoOlder = activeIndex < items.length - 1;
  const initials = (item.senderName || '新').trim().slice(0, 1).toUpperCase();

  return (
    <div className="fixed bottom-6 right-6 z-[10001] w-[min(380px,calc(100vw-32px))]">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onOpen(item.sessionKey)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(item.sessionKey);
          }
        }}
        className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/18 ring-1 ring-black/5 transition hover:-translate-y-0.5 hover:shadow-slate-900/25"
      >
        <div className="flex items-start gap-3 p-4">
          <div className="h-12 w-12 flex-none overflow-hidden rounded-2xl bg-slate-100 text-slate-700 ring-1 ring-slate-200">
            {item.avatar ? (
              <img src={item.avatar} alt={item.senderName} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-base font-extrabold">{initials}</div>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-extrabold text-slate-950">{item.senderName}</div>
                <div className="mt-0.5 text-xs font-semibold text-slate-500">{item.time}</div>
              </div>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onClose();
                }}
                className="flex h-8 w-8 flex-none items-center justify-center rounded-xl text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                aria-label="关闭消息提醒"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 line-clamp-3 break-words text-sm font-semibold leading-5 text-slate-800">
              {item.content}
            </div>
          </div>
        </div>

        {hasMultiple && (
          <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-4 py-2">
            <span className="text-xs font-bold text-slate-500">{activeIndex + 1} / {items.length}</span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  if (canGoNewer) onIndexChange(activeIndex - 1);
                }}
                disabled={!canGoNewer}
                className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-500 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="查看更新一条消息"
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  if (canGoOlder) onIndexChange(activeIndex + 1);
                }}
                disabled={!canGoOlder}
                className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-500 transition hover:bg-white hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="查看更早一条消息"
              >
                <ChevronDown className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [messageBadgeEnabled, setMessageBadgeEnabled] = useState(true);
  const [messagePopupEnabled, setMessagePopupEnabled] = useState(true);
  const [messageCenterTargetKey, setMessageCenterTargetKey] = useState('');
  const [messageNotifications, setMessageNotifications] = useState<MessageNotificationItem[]>([]);
  const [messageNotificationIndex, setMessageNotificationIndex] = useState(0);
  const [messageNotificationVisible, setMessageNotificationVisible] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const activeTabRef = useRef(activeTab);
  const messagePopupEnabledRef = useRef(messagePopupEnabled);
  const unreadRefreshTimerRef = useRef<number | null>(null);

  useEffect(() => {
      activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
      messagePopupEnabledRef.current = messagePopupEnabled;
  }, [messagePopupEnabled]);

  const refreshUnreadCount = useCallback(async () => {
      if (!isLoggedIn) return;
      try {
          const res = await getChatSessions({ limit: 300 });
          const total = (res.sessions || []).reduce((sum, session) => sum + Math.max(0, Number(session.unread_count || 0)), 0);
          setMessageUnreadCount(total);
      } catch {
          setMessageUnreadCount(0);
      }
  }, [isLoggedIn]);

  const loadMessageNotificationSettings = useCallback(async () => {
      if (!isLoggedIn) return;
      try {
          const settings = await getSystemSettings();
          setMessageBadgeEnabled(isSettingEnabled(settings.message_menu_badge_enabled, true));
          setMessagePopupEnabled(isSettingEnabled(settings.message_popup_enabled, true));
      } catch {
          // Keep current in-memory settings if the settings request fails.
      }
  }, [isLoggedIn]);

  const openMessageSession = useCallback((sessionKey: string) => {
      setMessageCenterTargetKey(sessionKey);
      setActiveTab('messages');
      setMessageNotificationVisible(false);
  }, []);

  const showDesktopMessageNotification = useCallback((item: MessageNotificationItem) => {
      if (typeof window === 'undefined' || !('Notification' in window)) return;

      const sendNotification = () => {
          const notification = new Notification(item.senderName, {
              body: item.content,
              icon: toAbsoluteNotificationUrl(item.avatar),
              tag: `xianyu-chat-${item.sessionKey}`,
              renotify: true,
              timestamp: item.rawTime ? new Date(item.rawTime.replace(' ', 'T')).getTime() : Date.now(),
          });
          notification.onclick = () => {
              window.focus();
              openMessageSession(item.sessionKey);
              notification.close();
          };
      };

      if (Notification.permission === 'granted') {
          sendNotification();
          return;
      }

      if (Notification.permission === 'default') {
          Notification.requestPermission()
            .then((permission) => {
                if (permission === 'granted') sendNotification();
            })
            .catch(() => undefined);
      }
  }, [openMessageSession]);

  const handleIncomingMessageNotification = useCallback((message: ChatMessage) => {
      const notification: MessageNotificationItem = {
          id: `${message.cookie_id}-${message.chat_id}-${message.raw_message_id || message.id || Date.now()}`,
          sessionKey: getChatKey(message),
          senderName: message.buyer_name || message.buyer_id || '新买家',
          content: messagePreview(message),
          time: formatNotificationTime(message.created_at),
          rawTime: message.created_at,
          avatar: getNotificationAvatar(message),
      };

      setMessageNotifications(current => {
          const next = [notification, ...current.filter(item => item.id !== notification.id)];
          return next.slice(0, 20);
      });
      setMessageNotificationIndex(0);
      setMessageNotificationVisible(true);
      showDesktopMessageNotification(notification);
  }, [showDesktopMessageNotification]);

  const scheduleUnreadRefresh = useCallback(() => {
      if (unreadRefreshTimerRef.current) {
          window.clearTimeout(unreadRefreshTimerRef.current);
      }
      unreadRefreshTimerRef.current = window.setTimeout(() => {
          refreshUnreadCount();
      }, 160);
  }, [refreshUnreadCount]);

  // Check auth on mount
  useEffect(() => {
      const handleLogout = () => {
          localStorage.removeItem('auth_token');
          setIsLoggedIn(false);
          setCheckingAuth(false);
          setActiveTab('dashboard');
          setLoginError('');
          setLoginLoading(false);
      };

      window.addEventListener(AUTH_LOGOUT_EVENT, handleLogout);

      const token = localStorage.getItem('auth_token');
      if (token) {
          verifyToken()
            .then(() => setIsLoggedIn(true))
            .catch(() => localStorage.removeItem('auth_token'))
            .finally(() => setCheckingAuth(false));
      } else {
          setCheckingAuth(false);
      }

      return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handleLogout);
  }, []);

  useEffect(() => {
      if (!isLoggedIn) {
          setMessageUnreadCount(0);
          setMessageNotifications([]);
          setMessageNotificationVisible(false);
          return;
      }

      loadMessageNotificationSettings();
      refreshUnreadCount();
      const handleSettingsUpdated = () => loadMessageNotificationSettings();
      window.addEventListener('system-settings:updated', handleSettingsUpdated);
      return () => window.removeEventListener('system-settings:updated', handleSettingsUpdated);
  }, [isLoggedIn, loadMessageNotificationSettings, refreshUnreadCount]);

  useEffect(() => {
      if (!messagePopupEnabled || activeTab === 'messages') {
          setMessageNotificationVisible(false);
      }
  }, [activeTab, messagePopupEnabled]);

  useEffect(() => {
      if (!isLoggedIn) return;

      const disconnect = connectChatEvents(
        (event) => {
          if (event.type === 'ready' || event.type === 'ping') return;

          if (event.type === 'order_auto_delivered') {
            window.dispatchEvent(new CustomEvent('orders:refresh-requested', { detail: event }));
            return;
          }

          if (event.type === 'item_auto_relisted') {
            window.dispatchEvent(new CustomEvent('items:refresh-requested', { detail: event }));
            return;
          }

          const message = event.message as ChatMessage | undefined;
          if (!message) return;

          scheduleUnreadRefresh();

          if (
            messagePopupEnabledRef.current &&
            activeTabRef.current !== 'messages' &&
            message?.direction === 'in'
          ) {
            handleIncomingMessageNotification(message);
          }
        },
        () => undefined
      );

      return () => {
        disconnect();
        if (unreadRefreshTimerRef.current) {
          window.clearTimeout(unreadRefreshTimerRef.current);
          unreadRefreshTimerRef.current = null;
        }
      };
  }, [handleIncomingMessageNotification, isLoggedIn, scheduleUnreadRefresh]);

  const handleLogin = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoginLoading(true);
      setLoginError('');
      
      try {
          const res = await login({ username, password });
          if (res.success && res.token) {
              localStorage.setItem('auth_token', res.token);
              setIsLoggedIn(true);
              refreshUnreadCount();
          } else {
              setLoginError(res.message || '登录失败');
          }
      } catch (err) {
          setLoginError('无法连接服务器');
      } finally {
          setLoginLoading(false);
      }
  };

  const handleTestEntry = () => {
      setLoginLoading(true);
      setTimeout(() => {
          localStorage.setItem('auth_token', 'test_token');
          setIsLoggedIn(true);
          setLoginLoading(false);
      }, 800);
  };

  if (checkingAuth) {
      return (
          <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7]">
              <ToastProvider />
              <Loader2 className="w-8 h-8 text-[#FFE815] animate-spin" />
          </div>
      );
  }

  // Login Screen Component
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F5F7] p-4 relative overflow-hidden font-sans">
        <ToastProvider />
        {/* Animated Background Blobs */}
        <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-yellow-200/40 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-200/30 rounded-full blur-[120px] animate-pulse" style={{animationDelay: '2s'}}></div>

        <div className="bg-white/80 backdrop-blur-3xl p-8 md:p-12 rounded-[3rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] w-full max-w-lg border border-white relative z-10 animate-fade-in">
          
          {/* Header with Logo */}
          <div className="text-center mb-10">
             <div className="w-24 h-24 bg-[#FFE815] rounded-[2rem] flex items-center justify-center shadow-xl shadow-yellow-200 mx-auto mb-6 transform rotate-[-6deg] hover:rotate-0 transition-all duration-500 cursor-pointer group">
                <span className="text-black font-extrabold text-5xl group-hover:scale-110 transition-transform">闲</span>
             </div>
             <h2 className="text-3xl font-extrabold text-gray-900 mb-2 tracking-tight">欢迎回来</h2>
             <p className="text-gray-500 font-medium">闲鱼智能自动发货与管家系统</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-4">
                <div className="relative group">
                    <User className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-black transition-colors" />
                    <input 
                        type="text" 
                        placeholder="管理员账号" 
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        className="w-full ios-input pl-14 pr-6 py-4.5 rounded-2xl text-base h-14"
                    />
                </div>
                <div className="relative group">
                    <Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-black transition-colors" />
                    <input 
                        type="password" 
                        placeholder="密码" 
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="w-full ios-input pl-14 pr-6 py-4.5 rounded-2xl text-base h-14"
                    />
                </div>
            </div>
            
            {loginError && (
                <div className="p-3 rounded-xl bg-red-50 text-red-500 text-sm text-center font-bold flex items-center justify-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> {loginError}
                </div>
            )}

            <button 
              type="submit" 
              disabled={loginLoading}
              className="w-full ios-btn-primary h-14 rounded-2xl text-lg shadow-xl shadow-yellow-200 mt-2 flex items-center justify-center gap-2 group disabled:opacity-70"
            >
              {loginLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>立即登录 <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" /></>}
            </button>
          </form>
          
          <div className="mt-8 pt-6 border-t border-gray-100">
             <button 
                type="button"
                onClick={handleTestEntry}
                disabled={loginLoading}
                className="w-full bg-black text-white h-14 rounded-2xl text-base font-bold shadow-lg shadow-gray-200 flex items-center justify-center gap-2 hover:bg-gray-800 transition-all active:scale-95"
             >
                <KeyRound className="w-5 h-5 text-[#FFE815]" />
                游客试用 (无需账号)
             </button>
             <div className="mt-6 text-center">
                 <span className="text-xs text-gray-400 font-medium tracking-widest uppercase">
                    Xianyu Auto-Dispatch Pro v2.5
                 </span>
             </div>
          </div>
        </div>
      </div>
    );
  }

  // Main App Layout
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <Dashboard />;
      case 'messages': return <MessageCenter targetSessionKey={messageCenterTargetKey} onUnreadChange={refreshUnreadCount} />;
      case 'accounts': return <AccountList />;
      case 'orders': return <OrderList />;
      case 'cards': return <CardList />;
      case 'items': return <ItemList />;
      case 'autoDelivery': return <AutoDelivery />;
      case 'keywords': return <Keywords />;
      case 'settings': return <Settings />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F4F5F7] text-[#111]">
      <ToastProvider />
      <Sidebar 
        activeTab={activeTab} 
        setActiveTab={(tab) => {
            setMessageCenterTargetKey('');
            setActiveTab(tab);
        }} 
        messageUnreadCount={messageBadgeEnabled ? messageUnreadCount : 0}
        onLogout={() => {
            localStorage.removeItem('auth_token');
            setIsLoggedIn(false);
        }} 
      />

      {activeTab === 'messages' ? (
        <main className="relative ml-64 h-screen flex-1 overflow-hidden">
          {renderContent()}
        </main>
      ) : (
        <main className="flex-1 ml-64 p-8 md:p-12 overflow-y-auto h-screen relative scroll-smooth">
          {/* Subtle background decoration */}
          <div className="fixed top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-yellow-50 to-transparent rounded-full blur-[120px] pointer-events-none -z-10 opacity-60"></div>

          <div className="max-w-[1400px] mx-auto pb-10">
              {renderContent()}
          </div>
        </main>
      )}

      {messagePopupEnabled && activeTab !== 'messages' && messageNotificationVisible && (
        <MessageNotificationCard
          items={messageNotifications}
          activeIndex={messageNotificationIndex}
          onIndexChange={setMessageNotificationIndex}
          onOpen={openMessageSession}
          onClose={() => setMessageNotificationVisible(false)}
        />
      )}
    </div>
  );
};

export default App;
